import React, { useState } from "react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { LoadingSpinner } from "../../../../../ui/LoadingSpinner";
import { Dropdown } from "../../../../../ui/dropdown/Dropdown";
import { DropdownItem } from "../../../../../ui/dropdown/DropdownItem";

interface MenuItemProps {
  item: {
    id: string;
    name: string;
    price: string;
    priceAmount: number;
    currencyCode: string;
    photo?: string | null;
    isavailable?: boolean;
  };
  onEdit: () => void;
  onDelete: () => void;
  onAvailabilityChange?: (itemId: string, isavailable: boolean) => void;
  isDeleting?: boolean;
  isEditing?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  onEdit,
  onDelete,
  onAvailabilityChange,
  isDeleting = false,
  isEditing = false,
}) => {
  const [isAvailabilityDropdownOpen, setIsAvailabilityDropdownOpen] = useState(false);

  return (
    <div className="flex items-center py-3">
      {/* Picture preview */}
      <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-4">
        {item.photo ? (
          <img
            src={item.photo}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <svg
            className="w-6 h-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        )}
      </div>
      {/* Item info */}
      <div className="flex-1 grid grid-cols-4 gap-2 items-center">
        <div className="col-span-2">{item.name}</div>
        <div>{`${item.currencyCode} ${(item.priceAmount || 0).toFixed(2)}`}</div>
        <div className="relative inline-block">
          {onAvailabilityChange ? (
            <>
              <button
                type="button"
                onClick={() => setIsAvailabilityDropdownOpen(!isAvailabilityDropdownOpen)}
                className="dropdown-toggle inline-flex items-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm px-4 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 text-gray-800 dark:text-white whitespace-nowrap"
              >
                {item.isavailable !== undefined && (
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      item.isavailable ? "bg-green-500" : "bg-red-500"
                    }`}
                  />
                )}
                <span>
                  {item.isavailable !== undefined
                    ? item.isavailable
                      ? "Available"
                      : "Unavailable"
                    : "Select"}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
              </button>
              <Dropdown
                isOpen={isAvailabilityDropdownOpen}
                onClose={() => setIsAvailabilityDropdownOpen(false)}
                className="min-w-[160px] z-[100]"
              >
                <DropdownItem
                  onClick={() => {
                    onAvailabilityChange(item.id, true);
                    setIsAvailabilityDropdownOpen(false);
                  }}
                  active={item.isavailable === true}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Available
                  </div>
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    onAvailabilityChange(item.id, false);
                    setIsAvailabilityDropdownOpen(false);
                  }}
                  active={item.isavailable === false}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Unavailable
                  </div>
                </DropdownItem>
              </Dropdown>
            </>
          ) : (
            <span className="text-xs text-gray-500">
              {item.isavailable !== undefined
                ? item.isavailable
                  ? "Available"
                  : "Unavailable"
                : "N/A"}
            </span>
          )}
        </div>
      </div>
      {/* Actions - positioned at the right end */}
      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={onEdit}
          disabled={isEditing || isDeleting}
          aria-label="Edit menu item"
          className="p-2 text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isEditing ? (
            <LoadingSpinner size="sm" color="primary" />
          ) : (
            <PencilIcon className="w-5 h-5" />
          )}
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting || isEditing}
          aria-label="Delete menu item"
          className="p-2 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDeleting ? (
            <LoadingSpinner size="sm" color="primary" />
          ) : (
            <TrashIcon className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default MenuItem;
